import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { getDistinct, getKeys, groupBy, sortDescending, toDict } from "swiipe.portal.shared"
import { calculateTagNamesForItems, getBaseTranslationLanguage } from "../../services/translationService"
import { ITranslationContextDto } from "../../type/translations/ITranslationContextDto"
import { ITranslationFileDto } from "../../type/translations/ITranslationFileDto"
import { ITranslationGlossaryEntryDto } from "../../type/translations/ITranslationGlossaryEntryDto"
import { ITranslationItem } from "../../type/translations/ITranslationItem"
import { ITranslationLanguageConfig } from "../../type/translations/ITranslationLanguageConfig"
import { ITranslationTagDto } from "../../type/translations/ITranslationTagDto"
import { ITranslationUsage } from "../../type/translations/ITranslationUsage"
import { getValues } from "../../util/objectUtils"

export interface ITranslationStoreAddition {
    translation: ITranslationSliceState
}

export type ETranslationChangeType = "PendingUpload" | "UserChange" | "TranslationService" | "Import"

export interface ITranslationChangeHistoryEntry {
    type: ETranslationChangeType
    value: string
    created: string // ISO date
}

export interface ITranslationChange {
    group: string
    language: string
    key: string
    value: string
    itemId: string
    type: ETranslationChangeType
    created: string // ISO date
    allChanges?: ITranslationChangeHistoryEntry[]
}

export interface ITranslationSelection {
    group: string
    languages?: string[]
    key: string
    itemId: string
}

export type ETranslationExportImportFileType = "json" | "csv"

interface ITranslationSliceState {
    languageConfigs?: ITranslationLanguageConfig[]
    files?: ITranslationFileDto[]
    items?: ITranslationItem[]
    itemsCalculatedTagNames: { [group: string]: { [key: string]: string[] } }
    tags: ITranslationTagDto[]
    lastAddedTagName?: string
    showAddTags: boolean
    showCalculatedTags: boolean
    showLastAddedTagSuggestion: boolean
    changes: ITranslationChange[]
    changesByItemId: { [itemId: string]: ITranslationChange[] }
    selections: ITranslationSelection[]
    currentLanguage: string
    export: {
        fileType: "json" | "csv"
    }
    showMissingTranslationCounters: boolean
    contexts?: ITranslationContextDto[]
    glossaryEntries?: { [toLang: string]: ITranslationGlossaryEntryDto[] }
    usage?: ITranslationUsage
}

const initialState: ITranslationSliceState = {
    changes: [],
    changesByItemId: {},
    selections: [],
    tags: [],
    itemsCalculatedTagNames: {},
    showAddTags: false,
    showCalculatedTags: false,
    showLastAddedTagSuggestion: false,
    currentLanguage: getBaseTranslationLanguage(),
    export: {
        fileType: "json",
    },
    showMissingTranslationCounters: false,
}

export const translationSlice = createSlice({
    name: "translation",
    initialState,
    reducers: {
        setLanguageConfigs: (state, action: PayloadAction<ITranslationLanguageConfig[]>) => {
            state.languageConfigs = action.payload
        },
        setCurrentLanguage: (state, action: PayloadAction<string>) => {
            state.currentLanguage = action.payload
        },
        setTranslationFiles: (state, action: PayloadAction<ITranslationFileDto[]>) => {
            state.files = action.payload
        },
        setTranslationItems: (state, action: PayloadAction<ITranslationItem[]>) => {
            state.items = action.payload
            if (state.tags.length > 0) {
                state.itemsCalculatedTagNames = calculateTagNamesForItems(state.items, state.tags)
            }
        },
        setShowAddTags: (state, action: PayloadAction<boolean>) => {
            state.showAddTags = action.payload
        },
        setShowCalculatedTags: (state, action: PayloadAction<boolean>) => {
            state.showCalculatedTags = action.payload
        },
        setTags: (state, action: PayloadAction<ITranslationTagDto[]>) => {
            state.tags = action.payload
            state.itemsCalculatedTagNames = calculateTagNamesForItems(state.items ?? [], state.tags)
        },
        addTags: (state, action: PayloadAction<ITranslationTagDto[]>) => {
            state.tags.push(...action.payload.filter((t) => !state.tags.some((st) => st.tagId === t.tagId)))
            const changedGroups = getDistinct(action.payload.map((t) => t.group))

            state.itemsCalculatedTagNames = {
                ...state.itemsCalculatedTagNames,
                ...calculateTagNamesForItems(
                    state.items?.filter((i) => changedGroups.includes(i.group)) ?? [],
                    state.tags.filter((t) => changedGroups.includes(t.group))
                ),
            }
        },
        removeTag: (state, action: PayloadAction<string>) => {
            const tag = state.tags.find((t) => t.tagId === action.payload)
            state.tags = state.tags.filter((t) => t.tagId !== action.payload)
            if (tag) {
                const changedGroups = [tag.group]

                state.itemsCalculatedTagNames = {
                    ...state.itemsCalculatedTagNames,
                    ...calculateTagNamesForItems(
                        state.items?.filter((i) => changedGroups.includes(i.group)) ?? [],
                        state.tags.filter((t) => changedGroups.includes(t.group))
                    ),
                }
            }
        },
        setLastAddedTagName: (state, action: PayloadAction<string | undefined>) => {
            state.lastAddedTagName = action.payload
        },
        setShowLastAddTagSuggestion: (state, action: PayloadAction<boolean>) => {
            state.showLastAddedTagSuggestion = action.payload
        },
        clearChanges: (
            state,
            action: PayloadAction<{ itemIds: string[]; language?: string; type?: ETranslationChangeType; created?: string }>
        ) => {
            for (let i = 0; i < action.payload.itemIds.length; i++) {
                const itemId = action.payload.itemIds[i]
                const itemChanges = state.changesByItemId[itemId]
                const otherItemsChanges = itemChanges?.filter(
                    (c) => !!action.payload.language && c.language !== action.payload.language
                )
                const relevantItemChanges = itemChanges?.filter(
                    (c) => !action.payload.language || c.language === action.payload.language
                )
                if (!action.payload.type && !action.payload.created) {
                    state.changesByItemId[itemId] = otherItemsChanges
                } else {
                    state.changesByItemId[itemId] = [
                        ...(otherItemsChanges ?? []),
                        ...(relevantItemChanges ?? [])
                            .map(
                                (c) =>
                                    ({
                                        ...c,
                                        allChanges: c.allChanges?.filter(
                                            (c) =>
                                                (!action.payload.type || c.type !== action.payload.type) &&
                                                (!action.payload.created || c.created !== action.payload.created)
                                        ),
                                    } satisfies ITranslationChange)
                            )
                            .filter((c) => (c.allChanges?.length ?? 0) > 0),
                    ]
                }
            }
            state.changes = getValues(state.changesByItemId).flat()
        },
        addChanges: (state, action: PayloadAction<{ changes: ITranslationChange[]; reset?: boolean }>) => {
            if (action.payload.reset) {
                state.changesByItemId = {}
                state.changes = []
            }
            for (let i = 0; i < action.payload.changes.length; i++) {
                const newChange = action.payload.changes[i]
                const itemOtherLanguages =
                    state.changesByItemId[newChange.itemId]?.filter((c) => c.language !== newChange.language) ?? []
                const changeForLanguage = state.changesByItemId[newChange.itemId]?.find((c) => c.language === newChange.language)
                const allChangesForLanguage = changeForLanguage?.allChanges ?? []
                const item = state.items?.find((i) => i.id === newChange.itemId)
                const itemValue = item?.translations.find((t) => t.language === newChange.language)?.content ?? ""

                let latestChanges = getDistinct(
                    sortDescending(
                        [
                            ...allChangesForLanguage,
                            {
                                created: newChange.created,
                                type: newChange.type,
                                value: newChange.value,
                            } satisfies ITranslationChangeHistoryEntry,
                        ],
                        (c) => c.created
                    ),
                    (c) => c.type
                )
                if (latestChanges.length > 1 && latestChanges[0].value === latestChanges[1].value) {
                    latestChanges = latestChanges.slice(1)
                }
                if (latestChanges.length === 1 && latestChanges[0].value === itemValue) {
                    latestChanges = latestChanges.slice(1)
                }
                if (latestChanges.length > 0) {
                    itemOtherLanguages.push({
                        ...newChange,
                        value: latestChanges[0].value,
                        type: latestChanges[0].type,
                        created: latestChanges[0].created,
                        allChanges: latestChanges,
                    } satisfies ITranslationChange)
                    state.changesByItemId[newChange.itemId] = itemOtherLanguages
                } else {
                    delete state.changesByItemId[newChange.itemId]
                }
            }
            state.changes = getValues(state.changesByItemId).flat()
        },
        changeSelection: (
            state,
            action: PayloadAction<{ language?: string; group?: string; keys?: string[]; isSelected: boolean }>
        ) => {
            const allLanguages = state.languageConfigs?.map((l) => l.language) || []
            const allLanguagesLength = allLanguages.length
            const { language, group, keys, isSelected } = action.payload
            const relatedSelections = state.selections.filter(
                (s) => (!group || s.group === group) && (!keys || keys?.includes(s.key))
            )
            state.selections = state.selections.filter(
                (s) => !((!group || s.group === group) && (!keys || keys?.includes(s.key)))
            )
            if (isSelected) {
                state.selections.push(
                    ...(state.items ?? [])
                        .filter((i) => (!group || i.group === group) && (!keys || keys?.includes(i.key)))
                        .map((i) => {
                            const related = relatedSelections.find((s) => s.itemId === i.id)
                            let languages =
                                (related && !related.languages) || !language
                                    ? undefined
                                    : (related?.languages ?? []).includes(language)
                                    ? related?.languages ?? []
                                    : [...(related?.languages ?? []), language]
                            if (languages && languages.length === allLanguagesLength) {
                                languages = undefined
                            }
                            return {
                                group: i.group,
                                key: i.key,
                                itemId: i.id,
                                languages: languages,
                            }
                        })
                )
            } else {
                state.selections.push(
                    ...relatedSelections
                        .map((s) => {
                            return {
                                ...s,
                                languages: !language
                                    ? []
                                    : !s.languages
                                    ? allLanguages.filter((l) => l !== language)
                                    : s.languages.filter((l) => l !== language),
                            }
                        })
                        .filter((s) => s.languages && s.languages.length > 0)
                )
            }
        },
        setExportFileType: (state, action: PayloadAction<ETranslationExportImportFileType>) => {
            state.export.fileType = action.payload
        },
        setShowMissingTranslationCounters: (state, action: PayloadAction<boolean>) => {
            state.showMissingTranslationCounters = action.payload
        },
        setContexts: (state, action: PayloadAction<ITranslationContextDto[]>) => {
            state.contexts = action.payload
        },
        setGlossaryEntries: (state, action: PayloadAction<ITranslationGlossaryEntryDto[]>) => {
            const groups = groupBy(action.payload, (ge) => ge.toLanguage)
            state.glossaryEntries = toDict(
                getKeys(groups),
                (toLang) => toLang,
                (toLang) => groups[toLang]
            )
        },
        setGlossaryEntriesForLangauge: (
            state,
            action: PayloadAction<{ language: string; entries: ITranslationGlossaryEntryDto[] }>
        ) => {
            state.glossaryEntries = {
                ...state.glossaryEntries,
                [action.payload.language]: action.payload.entries,
            }
        },
        setUsage: (state, action: PayloadAction<ITranslationUsage>) => {
            state.usage = action.payload
        },
    },
})

export const translationActions = translationSlice.actions
export const translationSelectors = {
    languageConfigs: (state: ITranslationStoreAddition) => state.translation.languageConfigs,
    files: (state: ITranslationStoreAddition) => state.translation.files,
    items: (state: ITranslationStoreAddition) => state.translation.items,
    changes: (state: ITranslationStoreAddition) => state.translation.changes,
    changesByItemId: (state: ITranslationStoreAddition, itemId: string) => state.translation.changesByItemId[itemId],
    tags: (state: ITranslationStoreAddition) => state.translation.tags,
    itemsCalculatedTagNames: (state: ITranslationStoreAddition) => state.translation.itemsCalculatedTagNames,
    showAddTags: (state: ITranslationStoreAddition) => state.translation.showAddTags,
    showCalculatedTags: (state: ITranslationStoreAddition) => state.translation.showCalculatedTags,
    lastAddedTagName: (state: ITranslationStoreAddition) => state.translation.lastAddedTagName,
    showLastAddedTagSuggestion: (state: ITranslationStoreAddition) => state.translation.showLastAddedTagSuggestion,
    selections: (state: ITranslationStoreAddition) => state.translation.selections,
    currentLanguage: (state: ITranslationStoreAddition) => state.translation.currentLanguage,
    exportFileType: (state: ITranslationStoreAddition) => state.translation.export.fileType,
    showMissingTranslationCounters: (state: ITranslationStoreAddition) => state.translation.showMissingTranslationCounters,
    contexts: (state: ITranslationStoreAddition) => state.translation.contexts,
    glossaryEntries: (state: ITranslationStoreAddition) => state.translation.glossaryEntries,
    usage: (state: ITranslationStoreAddition) => state.translation.usage,
}
